import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import {
  NzNotificationRef,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { finalize } from 'rxjs/operators';
import { GlobalValuesService, PartnerService, getSafeImageUrl } from '@app/core';
import {
  ConfirmationModalComponent,
  Partner,
  SetParentResult,
  ToastConfig,
} from '@app/shared';
import {
  buildPartialFailureMessage,
  buildSetParentFailureDetail,
  humanizeSetParentReason,
} from './set-parent-failure-report.utils';

/**
 * Seccion "Empleados asociados" de un contacto de tipo empresa: la vista inversa
 * de Partner.parentId (los empleados que apuntan a esta empresa).
 *
 * Se usa en dos modos sobre el mismo panel lateral:
 *  - editable = false -> vista de detalle, solo lectura, sin columna de accion.
 *  - editable = true  -> vista de edicion, con "Asociar empleados" (seleccion
 *    multiple en modal) e icono de desasociar por fila.
 *
 * La escritura va SIEMPRE contra el contacto hijo via PartnerService.setEmployeesParent,
 * nunca a traves del formulario de la empresa.
 */
@Component({
  selector: 'app-company-employees',
  standalone: false,
  templateUrl: './company-employees.component.html',
  styleUrl: './company-employees.component.less',
})
export class CompanyEmployeesComponent implements OnInit {
  @Input() companyId!: number;
  /** Nombre de la empresa, para dejar el censo global coherente al reasignar. */
  @Input() companyName = '';
  /** false -> detalle (solo lectura); true -> edicion (agregar y desasociar). */
  @Input() editable = false;

  @ViewChild('addEmployeesModalTmpl') addEmployeesModalTmpl!: TemplateRef<any>;
  @ViewChild('notificationTmpl') notificationTmpl!: TemplateRef<any>;
  @ViewChild('removeEmployeeConfirmationModal')
  removeEmployeeConfirmationModal!: ConfirmationModalComponent;

  private destroyRef = inject(DestroyRef);
  private destroyed = false;

  employees: Partner[] = [];
  loading = false;

  /** Estado del modal de asociar. */
  availableEmployees: Partner[] = [];
  filteredAvailableEmployees: Partner[] = [];
  selectedEmployeesIds = new Set<number>();
  employeeSearchText = '';
  loadingAvailable = false;
  savingEmployees = false;
  private addEmployeesModalRef: NzModalRef | null = null;

  /** Empleado pendiente de confirmar su desasociacion. */
  employeeToRemove: Partner | null = null;
  /** Empleado que se esta desasociando ahora mismo (spinner en su fila). */
  removingEmployeeId: number | null = null;

  notRef!: NzNotificationRef;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: '',
    showUndo: false,
  };

  protected readonly getSafeImageUrl = getSafeImageUrl;

  constructor(
    private partnerService: PartnerService,
    private globalValuesService: GlobalValuesService,
    private modalService: NzModalService,
    private notificationService: NzNotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => (this.destroyed = true));
    this.reload();
  }

  /**
   * Repinta la vista. Este componente vive dentro de un drawer (overlay) y no
   * se refresca solo al llegar una respuesta: sin esto el esqueleto se queda
   * puesto hasta el siguiente click. Mismo idiom que equipment-details-drawer.
   *
   * El guard no es decorativo: `takeUntilDestroyed` completa el flujo cuando se
   * destruye el componente, y entonces corre el `finalize` — tocar una vista ya
   * destruida lanzaria ViewDestroyedError.
   */
  private refreshView(): void {
    if (this.destroyed) return;
    this.cdr.detectChanges();
  }

  /** Recarga la lista de empleados de la empresa desde el servidor. */
  reload(): void {
    if (!this.companyId) {
      this.employees = [];
      return;
    }

    this.loading = true;
    this.partnerService
      .getEmployeesByCompany(this.companyId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.refreshView();
        })
      )
      .subscribe({
        next: (rows: Partner[]) => (this.employees = rows || []),
        error: (e: any) => {
          this.employees = [];
          this.showError('Error al cargar los empleados asociados.', e);
        },
      });
  }

  /** ---------- Modal de asociar empleados ---------- */

  /**
   * Los empleados disponibles se cargan ANTES de abrir el modal, no dentro.
   * La vista del modal se crea en el arbol del overlay, asi que un
   * detectChanges() de este componente no tiene por que alcanzarla y un
   * esqueleto interno podria quedarse colgado. Cargando primero, el modal se
   * abre con los datos ya puestos y no hay estado asincrono que repintar.
   * Mientras tanto el feedback lo da el propio boton "Asociar empleados".
   */
  openAddEmployeesModal(): void {
    if (this.loadingAvailable) {
      return;
    }

    this.selectedEmployeesIds.clear();
    this.employeeSearchText = '';
    this.availableEmployees = [];
    this.filteredAvailableEmployees = [];
    this.loadingAvailable = true;

    this.partnerService
      .getAvailableEmployees()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingAvailable = false;
          this.refreshView();
        })
      )
      .subscribe({
        next: (rows: Partner[]) => {
          this.availableEmployees = rows || [];
          this.filterAvailableEmployees();
          this.openModal();
        },
        error: (e: any) => {
          this.availableEmployees = [];
          this.filteredAvailableEmployees = [];
          this.showError('Error al cargar los empleados disponibles.', e);
        },
      });
  }

  private openModal(): void {
    this.addEmployeesModalRef = this.modalService.create({
      nzTitle: undefined,
      nzContent: this.addEmployeesModalTmpl,
      nzFooter: null,
      nzClosable: false,
      nzWidth: 600,
      nzCentered: true,
      nzMaskClosable: false,
    });
  }

  closeAddEmployeesModal(): void {
    this.addEmployeesModalRef?.close();
    this.addEmployeesModalRef = null;
    this.selectedEmployeesIds.clear();
    this.employeeSearchText = '';
  }

  onEmployeeSearchChange(searchText: string): void {
    this.employeeSearchText = searchText;
    this.filterAvailableEmployees();
  }

  private filterAvailableEmployees(): void {
    const search = this.employeeSearchText.trim().toLowerCase();
    if (!search) {
      this.filteredAvailableEmployees = [...this.availableEmployees];
      return;
    }

    this.filteredAvailableEmployees = this.availableEmployees.filter(
      (employee) => {
        const fullName = `${employee.name || ''} ${
          employee.surName || ''
        }`.toLowerCase();
        const email = (employee.email || '').toLowerCase();
        return fullName.includes(search) || email.includes(search);
      }
    );
  }

  toggleEmployeeSelection(id: number, checked: boolean): void {
    if (checked) {
      this.selectedEmployeesIds.add(id);
    } else {
      this.selectedEmployeesIds.delete(id);
    }
  }

  /** Asocia de una vez todos los empleados marcados. */
  confirmAddEmployees(): void {
    if (this.selectedEmployeesIds.size === 0) {
      return;
    }

    const operations = [...this.selectedEmployeesIds].map((partnerId) => ({
      partnerId,
      parentId: this.companyId,
    }));
    const total = operations.length;

    this.savingEmployees = true;
    this.partnerService
      .setEmployeesParent(operations)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.savingEmployees = false;
          this.refreshView();
        })
      )
      .subscribe({
        next: (result: SetParentResult) => {
          this.closeAddEmployeesModal();
          this.reportSetParentResult(result, total);
          this.syncGlobalPartners(result.succeeded, {
            id: this.companyId,
            name: this.companyName,
          });
          // El estado real siempre se relee del servidor: si el lote quedo a
          // medias, la lista muestra lo que de verdad hay, no lo que se pidio.
          this.reload();
        },
        error: (e: any) => {
          this.closeAddEmployeesModal();
          this.showError('No se pudieron asociar los empleados.', e);
          this.reload();
        },
      });
  }

  /**
   * Un lote de N llamadas puede salir bien del todo, a medias o mal del todo.
   * El aviso dice cual de las tres, y en los dos ultimos casos el motivo que
   * devolvio el backend (CEC-640, criterio 7: el usuario ve POR QUE).
   */
  private reportSetParentResult(result: SetParentResult, total: number): void {
    const ok = result.succeeded.length;
    const failed = result.failed.length;

    if (!failed) {
      this.showSuccess(
        `${ok} ${ok === 1 ? 'empleado asociado' : 'empleados asociados'} correctamente.`
      );
      return;
    }

    // El motivo crudo se queda en consola para quien de soporte: en pantalla va
    // traducido, y console.warn sobrevive al recorte de logs de main.ts.
    console.warn('Fallos al asociar empleados', result.failed);

    const detail = buildSetParentFailureDetail(result.failed, (id) =>
      this.resolveEmployeeName(id)
    );

    if (!ok) {
      this.showError(
        total === 1
          ? 'No se pudo asociar el empleado.'
          : 'No se pudo asociar ninguno de los empleados.',
        { error: { message: detail } }
      );
      return;
    }

    this.showWarning(buildPartialFailureMessage(ok, total, failed), detail);
  }

  /**
   * Nombre de un contacto a partir de su id, buscando en las dos listas que el
   * componente ya tiene cargadas: la del modal (al asociar) y la de empleados
   * de la empresa (al desasociar).
   */
  private resolveEmployeeName(partnerId: number): string {
    const employee =
      this.availableEmployees.find((e) => e.id === partnerId) ||
      this.employees.find((e) => e.id === partnerId);

    return employee ? this.getFullName(employee) : '';
  }

  /** ---------- Desasociar ---------- */

  askRemoveEmployee(employee: Partner): void {
    this.employeeToRemove = employee;
    this.removeEmployeeConfirmationModal.config = {
      title: 'Desasociar empleado',
      message: `¿Seguro que quieres desasociar a ${this.getFullName(
        employee
      )} de esta empresa? El contacto no se elimina, solo se desvincula.`,
      confirmText: 'Desasociar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.removeEmployeeConfirmationModal.open();
  }

  onRemoveEmployeeConfirmed(): void {
    const employee = this.employeeToRemove;
    if (!employee) {
      return;
    }
    this.employeeToRemove = null;
    this.removingEmployeeId = employee.id;

    // parentId null desvincula; no borra el contacto ni toca sus demas campos.
    this.partnerService
      .setEmployeesParent([{ partnerId: employee.id, parentId: null }])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.removingEmployeeId = null;
          this.refreshView();
        })
      )
      .subscribe({
        next: (result: SetParentResult) => {
          if (result.failed.length) {
            console.warn('Fallo al desasociar empleado', result.failed);
            this.showError('No se pudo desasociar el empleado de la empresa.', {
              error: {
                message: humanizeSetParentReason(result.failed[0].reason),
              },
            });
          } else {
            this.showSuccess(
              `${this.getFullName(employee)} ya no pertenece a esta empresa.`
            );
            this.syncGlobalPartners([employee.id], null);
          }
          this.reload();
        },
        error: (e: any) => {
          this.showError('No se pudo desasociar el empleado de la empresa.', e);
          this.reload();
        },
      });
  }

  /**
   * La asociacion se escribe en el contacto HIJO, y el listado de Contactos se
   * pinta desde el censo en memoria de GlobalValuesService (partner.component
   * lee `partnerlist`, no vuelve a consultar al navegar). Sin esto, el listado
   * seguiria mostrando la empresa anterior hasta recargar la aplicacion.
   */
  private syncGlobalPartners(
    partnerIds: number[],
    parent: { id: number; name: string } | null
  ): void {
    this.globalValuesService.updatePartnersParent(partnerIds, parent);
  }

  /** ---------- Utilidades de presentacion ---------- */

  trackByEmployeeId(_index: number, employee: Partner): number {
    return employee.id;
  }

  getFullName(employee: Partner): string {
    return `${employee?.name || ''} ${employee?.surName || ''}`.trim() || 'Sin nombre';
  }

  getInitials(employee: Partner): string {
    const firstName = employee?.name || '';
    const lastName = employee?.surName || '';

    if (firstName && !lastName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    if (firstName && lastName) {
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }
    return firstName ? firstName.charAt(0).toUpperCase() : 'E';
  }

  /** ---------- Avisos ---------- */

  private showSuccess(message: string): void {
    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content: message,
      showUndo: false,
    };
    this.showNotification();
  }

  /**
   * El motivo real del backend viaja en e.error.message: lo garantiza
   * standarize-errors.interceptor, que ademas prefiere validationErrors[0].reason.
   */
  private showError(content: string, error: any): void {
    console.error(content, error);
    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content,
      showUndo: false,
      msg: error?.error?.message,
    };
    this.showNotification();
  }

  /** Exito parcial: ni verde ni rojo. */
  private showWarning(content: string, detail?: string): void {
    this.toastConfig = {
      icon: 'warning',
      iconColor: '#FF990A',
      content,
      showUndo: false,
      msg: detail,
    };
    this.showNotification();
  }

  private showNotification(): void {
    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight' }
    );
  }
}
